import { FormPreviewComponent } from "./designComponent/FormPreviewComponent";
import React from "react";
import Processing from "./commonComponent/ProcessingComponent";
import { FormSectionElements, FormSections, ProgramContainer } from "./entities/formTemplateDataType";
import { RuntimeHeaderComponent } from "./commonComponent/RuntimeHeaderComponent";
import { RuntimeFooterComponent } from "./commonComponent/RuntimeFooterComponent";
import ReactGA from "react-ga4";
import { ServiceOntarioHeaderComponent } from "./commonComponent/ServiceOntarioHeaderComponent";
import { SessionEndComponent } from "./SessionEndComponent";
import { ApplicationHeaderComponent } from "./commonComponent/ApplicationHeaderComponent";
import {
  getCxpMessages,
  getUserProfileFromSession,
} from "./services/CxpProfileService";
import DynamicScriptContent from "./commonComponent/DynamicScriptComponent";
import {
  AUTH_TOKENS,
  MaintainableApps,
  Scripts,
  TOKEN_TYPE,
  USER_PROFILE,
} from "./entities/Constants";
import {
  checkProfileAccount,
  fetchSessionFromServer,
  cleanup,
  signin,
  valifyToken,
  getAndValidateAdvanceFlag,
  consumeAdvanceFlag,
} from "./security/PublicSecure";
import {
  signin as bpsSignin,
  checkProfileAccount as bpsCheckProfileAccount,
  getTokens as bpsGetTokens,
  getBpsConfig,
  verifyBpsToken,
  getBpsAccessToken,
  clearSessionStorage as bpsClearSessionStorage,
} from "./security/BpsSecure";
import SessionStorageService from "./services/SessionStorageService";
import URLUtils, { getAppNameFromPath, pushSectionUrlToHistory, combineUrl } from "./utilities/URLUtils";
import axios from "axios";
import Maintenance from "./commonComponent/MantenanceComponent";
import * as CircularJSON from "circular-json";
import { getAdminConfig } from "./services/EnvConfigService";
import { Toast } from "primereact/toast";
import { isRuntime } from "./config";
import { RuntimeWithPageRedirect } from "./RuntimeWithPageRedirect";
import { setGlobalProfile } from "./services/ProfileHooks";
import DataUtils, { populateProgramContainer } from "./utilities/DataUtils";
import { LabourHeaderComponent } from "./commonComponent/LabourHeaderComponent";
import { MCCSSHeaderComponent } from "./commonComponent/MCCSSHeaderComponent";
import { getPublicSecurePage } from "./utilities/ElementUtils";
import SectionUtils from "./utilities/SectionUtils";
import { setBaseHeaders } from "./utilities/submitUtils";
import FormValidationUtils from "./utilities/FormValidationUtils";
import TrackPageView from "./utilities/TrackPageView";
import { da } from "date-fns/locale";

import {
  getInitialStepperIndexBasedOnReferer,
  hideFirstPageIfRefererIsOntarioCa,
} from "./utilities/RefererUtils";
import { getEnvVar, ENV } from "./config/RuntimeConfig";

import { handleAzure401 } from "./security/AzureAuthService";

interface Props {
  programContainer: ProgramContainer;
  stepperIndex?: number;

}

interface State {
  programContainer: ProgramContainer;
  isEng: boolean;
  showSessionEndPage: boolean;
  stepperIndex?: number;
  entityVersion: number;
  headerClickNonce?: number;
  isInitializingClientScript?: boolean;
  isProfileLoaded: boolean;
}

export class RuntimeComponent extends React.Component<Props, State> {
  growl: Toast;
  autoSaveTimer: any;
  private sessionMonitor?: number;
  private bpsSessionMonitor?: number;
  private sessionExpiredRedirect = false;
  private initialLandingURL: string = "";
  private isIntentionalExit: boolean = false;
  private entityObserverAttachedTo?: ProgramContainer;
  private hasMounted = false;
  private headerInitScriptRun = false;
  private initialDataApiRun = false;
  private publicSecurePreScriptRun = false;
  private initialClientScriptPromise?: Promise<void>;
  private pendingClientScriptBlock?: boolean;

  private syncProfileToken(programContainer: ProgramContainer) {
    const hydratedProfile = SessionStorageService.hydrateProfileWithPSToken(
      programContainer?.Profile,
    );
    if (hydratedProfile !== programContainer?.Profile) {
      programContainer.Profile = hydratedProfile;
    }
    return programContainer;
  }

  private updateUrlForStep(stepperIndex: number) {
    const programContainer =
      this.state.programContainer ?? this.props.programContainer;
    const sections = programContainer?.ListOfFormSections;

    if (
      !sections ||
      stepperIndex == null ||
      stepperIndex < 0 ||
      stepperIndex >= sections.length
    ) {
      return;
    }

    if (
      getEnvVar('IS_RUNTIME') !== "true" &&
      getEnvVar('IS_RUNTIME') !== "withLayout"
    ) {
      return;
    }

    const section = sections[stepperIndex];

    if (!section?.ENUTitle) {
      return;
    }

    pushSectionUrlToHistory(programContainer, section, stepperIndex, getEnvVar('IS_RUNTIME'));
  }

  constructor(props) {
    super(props);

    let isEng = this.props.programContainer.Profile?.lang !== "fr";

    const url = window.location.href;

    // Capture the initial landing URL with code and state parameters
    this.initialLandingURL = url;

    if (url.indexOf("/fr") > 0) {
      isEng = false;
    }

    //populateElementFindRoot(this.props.programContainer);

    let initialStepperIndex =
      typeof this.props.stepperIndex === "number" ? this.props.stepperIndex : 0;
    const initialClientScript = this.props.programContainer.InitialClientScript;
    if (
      typeof initialClientScript === "string" &&
      initialClientScript.trim().length > 0
    ) {
      try {
        const customJSFunc = eval(`(${initialClientScript})`);
        if (typeof customJSFunc === "function") {
          const result = customJSFunc(this.props.programContainer, window);
          if (typeof result === "number" && Number.isFinite(result)) {
            initialStepperIndex = result;
          } else if (
            this.props.programContainer.InitialClientScriptAwait &&
            result != null &&
            typeof (result as any).then === "function"
          ) {
            this.initialClientScriptPromise = result as Promise<void>;
            this.pendingClientScriptBlock = true;
          }
        }
      } catch (ex) {
        console.error("Error in InitialClientScript:", ex);
      }
    }

    this.state = {
      isEng: this.props.programContainer.EnglishOnly || isEng,
      programContainer: this.props.programContainer,
      showSessionEndPage: false,
      stepperIndex: initialStepperIndex || 0,
      entityVersion: 0,
      headerClickNonce: 0,
      isInitializingClientScript: this.pendingClientScriptBlock === true,
      isProfileLoaded: false,
    };

    // Ensure a clean baseline on load (e.g., after Public Secure sign-in or draft hydrate).
    SectionUtils.resetDirtyFlags(this.props.programContainer);

    this.attachIsDirtyAccessor(this.props.programContainer);
    this.attachEntityChangeObserver(this.props.programContainer);

    if (this.props.programContainer.GATrackingID) {
      ReactGA.initialize(this.props.programContainer.GATrackingID);
    }

    this.loadGTM(this.props.programContainer);
  }

  private loadGTM(programContainer: ProgramContainer) {
    const gaTrackingId = programContainer.GATrackingID?.trim();
    const gtmId = programContainer.GTMContainerId || (!gaTrackingId ? this.resolveDefaultGTMId(programContainer) : undefined);
    if (!gtmId) return;

    // Standard GTM snippet
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = document.getElementsByTagName('script')[0];
    const j = document.createElement('script');
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    f.parentNode?.insertBefore(j, f);
  }

  private resolveDefaultGTMId(programContainer: ProgramContainer): string | undefined {
    const hosted = programContainer.Hosted;
    if (hosted === 'BXP') return 'GTM-PT9LD6S4';
    if (hosted === 'CXP') return 'GTM-WF8CFXCC';
    return undefined;
  }

  private attachIsDirtyAccessor(programContainer?: ProgramContainer | null) {
    if (!programContainer) {
      return;
    }

    const descriptor = Object.getOwnPropertyDescriptor(programContainer, "IsDirty");
    if (descriptor?.get) {
      return;
    }

    Object.defineProperty(programContainer, "IsDirty", {
      configurable: true,
      enumerable: descriptor?.enumerable ?? true,
      get: () => SectionUtils.isFormDirty(programContainer),
      set: (value: boolean) => {
        if (value === false) {
          SectionUtils.resetDirtyFlags(programContainer);
        }
      },
    });
  }

  private attachEntityChangeObserver(programContainer?: ProgramContainer | null) {
    if (!programContainer) {
      return;
    }

    if (this.entityObserverAttachedTo === programContainer) {
      return;
    }

    const descriptor = Object.getOwnPropertyDescriptor(programContainer, "Entity");
    if (descriptor?.get || descriptor?.set) {
      this.entityObserverAttachedTo = programContainer;
      return;
    }

    let currentEntity = programContainer.Entity;
    Object.defineProperty(programContainer, "Entity", {
      configurable: true,
      enumerable: true,
      get: () => currentEntity,
      set: (next) => {
        if (next === currentEntity) return;
        currentEntity = next;
        if (this.hasMounted) {
          this.setState((prev) => ({
            entityVersion: (prev.entityVersion ?? 0) + 1,
          }));
        }
      },
    });

    this.entityObserverAttachedTo = programContainer;
  }
  autoSave = async () => {
    this.autoSaveTimer = setInterval(() => {
      DataUtils.saveDraftToSession(this.props.programContainer).then((success) => {
        if (success) console.log("Draft saved automatically");
      });
    }, 30000); // 30000ms = 0.5 minute
  };

  // // Extract only user input values from form sections, excluding navigation/visibility state and buttons
  // extractFormValues = (programContainer) => {
  //   if (!programContainer?.ListOfFormSections) {
  //     return {};
  //   }

  //   const values = {};

  //   // Element types that should NOT trigger dirty check
  //   const excludedTypes = [
  //     'label',
  //     'html',
  //     'button',
  //     'submit',
  //     'reset',
  //     'navigation',
  //     'stepper',
  //     'link'
  //   ];

  //   const extractElementValues = (elements, path = '') => {
  //     if (!elements || !Array.isArray(elements)) {
  //       return;
  //     }

  //     elements.forEach((element) => {
  //       const elementType = (element.Datatype || element.Type || '').toLowerCase();
  //       const elementName = (element.Name || '').toLowerCase();
  //   delete element.day;
  //                    delete element.month;
  //                       delete element.year;
  //       // Skip excluded types
  //       if (elementType && excludedTypes.includes(elementType)) {
  //         return;
  //       }

  //       // Skip elements with "button" in their name or type
  //       if (elementName.includes('button') || elementType.includes('button')) {
  //         return;
  //       }

  //       // Skip title/heading elements
  //       if (elementName.includes('title') || elementName.includes('heading') ||
  //           elementType.includes('title') || elementType.includes('heading')) {
  //         return;
  //       }

  //       // Track all elements that have a LoadBindName (binding field name)
  //       if (element.LoadBindName) {
  //         const key = element.LoadBindName;
  //         values[key] = {
  //           Value: element.Value,
  //           fieldValue: element.fieldValue,
  //           day: element.day,
  //           month: element.month,
  //           year: element.year
  //         };
  //       }

  //       // Recursively check subsections
  //       if (element.ListOfFormSubSection) {
  //         element.ListOfFormSubSection.forEach((subSection) => {
  //           if (subSection.ListOfFormSubSectionEle) {
  //             extractElementValues(subSection.ListOfFormSubSectionEle, `${path}.${element.Id}`);
  //           }
  //         });
  //       }
  //     });
  //   };

  //   programContainer.ListOfFormSections.forEach((section) => {
  //     // Check for direct elements on section
  //     if (section.ListOfFormSectionElements && Array.isArray(section.ListOfFormSectionElements)) {
  //       extractElementValues(section.ListOfFormSectionElements, section.Id);
  //     }

  //     // Also check subsections
  //     if (section.ListOfFormSubSections && Array.isArray(section.ListOfFormSubSections)) {
  //       section.ListOfFormSubSections.forEach((subSection) => {
  //         if (subSection.ListOfFormSectionElements) {
  //           extractElementValues(subSection.ListOfFormSectionElements, section.Id);
  //         }
  //       });
  //     }
  //   });

  //   return values;
  // };

  // // Capture initial form data for dirty checking
  // captureInitialFormData = () => {
  //   try {
  //     // Only compare actual user input values, exclude navigation/visibility state
  //     const formValues = this.extractFormValues(this.state.programContainer);
  //     this.initialFormValues = formValues;
  //     // Use JSON.stringify since we're only comparing simple values now
  //     this.initialFormData = JSON.stringify(formValues);

  //     // Reset IsDirty flags on all fields based on the current baseline
  //     SectionUtils.applyDirtyFlags(
  //       this.state.programContainer,
  //       this.initialFormValues,
  //       formValues,
  //     );
  //   } catch (error) {
  //     console.error("Error capturing initial form data:", error);
  //   }
  // };

  // // Check if form is dirty by comparing current state with initial state
  // checkIfFormIsDirty = (): boolean => {
  //   try {
  //     if (!this.initialFormData) {
  //       return false;
  //     }
  //     // Only compare actual user input values, exclude navigation/visibility state
  //     const currentValues = this.extractFormValues(this.state.programContainer);

  //      // Update IsDirty flags on individual fields
  //      if (this.initialFormValues) {
  //        SectionUtils.applyDirtyFlags(
  //          this.state.programContainer,
  //          this.initialFormValues,
  //          currentValues,
  //        );
  //      }

  //     const currentFormData = JSON.stringify(currentValues);
  //     const isDirty = currentFormData !== this.initialFormData;
  //     return isDirty;
  //   } catch (error) {
  //     console.error("Error checking if form is dirty:", error);
  //     return false;
  //   }
  // };

  // // Mark form as dirty
  // markFormAsDirty = () => {
  //   if (!this.state.isFormDirty) {
  //     this.setState({ isFormDirty: true });
  //   }
  // };

  // // Mark form as clean
  // markFormAsClean = () => {
  //   if (this.state.isFormDirty) {
  //     this.setState({ isFormDirty: false });
  //   }
  // };

  // // Clear dirty flag (e.g., after successful save/submit)
  // clearDirtyFlag = () => {
  //   this.captureInitialFormData();
  //   this.setState({ isFormDirty: false });
  // };

  // // Handle browser beforeunload event to warn about unsaved changes
  // handleBeforeUnload = (e: BeforeUnloadEvent) => {
  //   // Skip warning if user has confirmed exit through custom dialog
  //   if (this.isIntentionalExit) {
  //     return;
  //   }

  //   if (this.state.isFormDirty) {
  //     e.preventDefault();
  //     e.returnValue = ''; // Required for Chrome
  //   }
  // };

  // Allow navigation without beforeunload warning (called before intentional exit)
  // allowExit = () => {
  //   this.isIntentionalExit = true;
  // };

  async componentDidMount() {
    console.log('[componentDidMount] RuntimeComponent mounting');
    console.log('[componentDidMount] Current URL:', window.location.href);
    console.log('[componentDidMount] localStorage advanceAfterCxpReturn:', localStorage.getItem('advanceAfterCxpReturn'));
    
    // Pause tracking immediately if:
    // 1. User profile exists (user is already signed in)
    // 2. Returning from OAuth (has ?code= parameter - profile will be loaded soon)
    const userProfileExists = !!(sessionStorage.getItem("userProfile") || this.props.programContainer.Profile?.userId);
    const hasOAuthCode = window.location.search.includes("code=");
    const hasAdvanceFlag = !!localStorage.getItem('advanceAfterCxpReturn');
    
    if (userProfileExists || hasOAuthCode || hasAdvanceFlag) {
      console.log('[componentDidMount] Pausing tracking during initialization:', {
        userProfileExists,
        hasOAuthCode,
        hasAdvanceFlag,
      });
      sessionStorage.setItem("gaUploadPause", "true");
    }
    
    this.hasMounted = true;
    // Clean up any stale advanceAfterCxpReturn flags on mount
    this.cleanupStaleAdvanceFlag();
    SectionUtils.hydrateParentReferences(this.props.programContainer);
    this.attachEntityChangeObserver(
      this.state.programContainer || this.props.programContainer,
    );
    this.syncProfileToken(this.props.programContainer);
    
    console.log('[componentDidMount] About to call getProfile');
    await this.getProfile(this.props.programContainer);
    console.log('[componentDidMount] getProfile completed');
    
    window.addEventListener("storage", (event: any) => {
      if (event.key === "userProfile") {
        console.log(event.newValue);
        if (event.newValue) {
          console.log(`localStorage new item added: ${event.key}`);
          //this.props.programContainer.Profile = JSON.parse(event.newValue);
        } else if (this.props.programContainer.Profile) {
          console.log(`localStorage cleared: ${event.key}`);
          //this.props.programContainer.Profile = null;
        }
      }
    });
    window.addEventListener("mousemove", (event) => {
      event.preventDefault();
    });

    let isEng = this.state.isEng ?? true;

    let refreshProfile = false;
    if (
      !this.props.programContainer.Profile?.firstName &&
      !this.props.programContainer.Profile?.lastName
    ) {
      const userProfile = getUserProfileFromSession(); // localStorage.getItem('userProfile'); // || sessionStorage.getItem('chcInfo');
      isEng = userProfile?.lang !== "fr";
      this.props.programContainer.Profile = SessionStorageService.hydrateProfileWithPSToken(userProfile);
      console.log("get usr profile in runtime:", userProfile);
      if (userProfile?.userId && !userProfile?.token) {
        const programContainer = await fetchSessionFromServer(
          this.props.programContainer,
        );
        this.props.programContainer.Profile = programContainer.Profile;
      }
      if (this.props.programContainer.Profile?.token) {
        const messages = await getCxpMessages(
          this.props.programContainer.Profile.token,
          { pageNumber: 1, pageSize: 5, search: "" },
        );
        this.props.programContainer.Profile.messages = messages.messages;
      }

      if (
        MaintainableApps.includes(
          this.props.programContainer.CategoryId.toLowerCase(),
        )
      ) {
        const adminConfig = await getAdminConfig(
          this.props.programContainer.CategoryId,
        );
        this.props.programContainer.adminConfig = adminConfig;
      }
      this.setState({ programContainer: this.props.programContainer }, () => {
        setGlobalProfile(this.props.programContainer.Profile);
      });
    }
    const url = window.location.href;

    if (url.indexOf("/fr/") > 0 && !this.props.programContainer.EnglishOnly) {
      isEng = false;
    }

    if (isEng !== this.state.isEng) {
      this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng });
    }

    if (this.props.programContainer.AutoSavedEnabled) {
      console.log("start auto save timer");
      this.autoSave();
    }

    if (this.hasDraftToRestore(this.props.programContainer)) {
      await this.retrieveDraft();
    }

    this.startPublicSecureSessionMonitor();
    this.startBpsSessionMonitor();

  }

  componentWillUnmount(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    this.stopPublicSecureSessionMonitor();
    this.stopBpsSessionMonitor();
    // Clean up the advance flag on unmount to prevent stale data
    localStorage.removeItem('advanceAfterCxpReturn');
    this.hasMounted = false;
  }

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any,
  ): void {
    if (this.props.programContainer !== prevProps.programContainer) {
      SectionUtils.hydrateParentReferences(this.props.programContainer);
    }
    if (
      this.props.programContainer !== prevProps.programContainer ||
      this.state.programContainer !== prevState.programContainer
    ) {
      this.attachIsDirtyAccessor(this.state.programContainer || this.props.programContainer);
      this.attachEntityChangeObserver(this.state.programContainer || this.props.programContainer);
    }

    if (
      this.props.programContainer.Profile !== prevProps.programContainer.Profile
    ) {
      this.setState({
        programContainer: this.props.programContainer,
      });
    }

    if (this.state.isEng == undefined) this.setState({ isEng: true });

    this.startPublicSecureSessionMonitor();
    this.startBpsSessionMonitor();
  }

  private requiresPublicSecureAuth = (): boolean => {
    const authRequired =
      this.state.programContainer?.AuthRequired ??
      this.props.programContainer?.AuthRequired;
    return typeof authRequired === "string" && authRequired.toLowerCase() === "ps";
  };

  private requiresBpsAuth = (): boolean => {
    const authRequired =
      this.state.programContainer?.AuthRequired ??
      this.props.programContainer?.AuthRequired;
    return typeof authRequired === "string" && authRequired.toLowerCase() === "bps";
  };

  private hasAuthenticatedPublicSecureProfile = (): boolean => {
    const profile =
      this.state.programContainer?.Profile ?? this.props.programContainer?.Profile;
    return Boolean(profile?.userId || profile?.token);
  };

  private hasAuthenticatedBpsProfile = (): boolean => {
    const profile =
      this.state.programContainer?.Profile ?? this.props.programContainer?.Profile;
    const bpsToken = getBpsAccessToken();
    return Boolean(profile?.token || (bpsToken && verifyBpsToken(bpsToken)));
  };

  private getSelectedDraftId = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get("draftId") || params.get("id");
  };

  private hasDraftToRestore(programContainer?: ProgramContainer | null): boolean {
    const selectedDraftId = this.getSelectedDraftId();
    if (selectedDraftId) {
      return true;
    }
    if (!programContainer?.CategoryId) {
      return false;
    }
    if (this.readDraftPayloadFromIdStorage(programContainer.CategoryId)) {
      return true;
    }
    if (!programContainer?.saveLaterEnabled) {
      return false;
    }
    if (sessionStorage.getItem(`draft_${programContainer.CategoryId}`)) {
      return true;
    }
    return Boolean(this.readDraftIdFromStorage(programContainer.CategoryId));
  }

  private getDraftIdStorageKey = (categoryId?: string | null) => {
    return categoryId ? `draftId_${categoryId}` : null;
  };

  private persistDraftId = (draftId: string | null | undefined) => {
    if (!draftId) {
      return;
    }
    this.props.programContainer.draftId = draftId;
    if (this.state.programContainer) {
      this.state.programContainer.draftId = draftId;
    }
    const key = this.getDraftIdStorageKey(this.props.programContainer.CategoryId);
    if (key) {
      sessionStorage.setItem(key, draftId);
    }
  };

  private readDraftIdFromStorage = (categoryId: string) => {
    const key = this.getDraftIdStorageKey(categoryId);
    if (!key) {
      return null;
    }
    const stored = sessionStorage.getItem(key);
    const parsed = this.tryParseDraftPayload(stored);
    const normalized = this.unwrapDraftPayload(parsed);
    if (this.isDraftPayload(normalized)) {
      return null;
    }
    return stored;
  };

  private tryParseDraftPayload = (raw: string | null) => {
    if (!raw) {
      return null;
    }
    try {
      return CircularJSON.parse(raw);
    } catch (error) {
      return null;
    }
  };

  private isDraftPayload = (payload: any): boolean => {
    return (
      payload != null &&
      typeof payload === "object" &&
      Object.prototype.hasOwnProperty.call(payload, "data")
    );
  };

  private unwrapDraftPayload = (payload: any): any => {
    if (!payload || typeof payload !== "object") {
      return payload;
    }
    if (!Object.prototype.hasOwnProperty.call(payload, "formValue")) {
      return payload;
    }
    const formValue = payload.formValue;
    if (typeof formValue === "string") {
      return this.tryParseDraftPayload(formValue) ?? formValue;
    }
    return formValue;
  };

  private isSectionedDraftData = (data: any): boolean => {
    if (!data || typeof data !== "object") {
      return false;
    }
    return Object.keys(data).some((key) => key.startsWith("Section_"));
  };

  private tryHydrateDraftPayload = (
    rawPayload: unknown,
    programContainer: ProgramContainer,
  ): boolean => {
    if (!rawPayload) {
      return false;
    }
    const parsedPayload =
      typeof rawPayload === "string"
        ? this.tryParseDraftPayload(rawPayload)
        : rawPayload;
    const normalizedPayload = this.unwrapDraftPayload(parsedPayload);
    if (!this.isDraftPayload(normalizedPayload)) {
      return false;
    }
    const payloadString =
      typeof normalizedPayload === "string"
        ? normalizedPayload
        : CircularJSON.stringify(normalizedPayload);
    try {
      const payloadData = normalizedPayload?.data;
      if (typeof normalizedPayload?.stepperIndex === "number") {
        programContainer.resumeStepperIndex = normalizedPayload.stepperIndex;
      }

      if (this.isSectionedDraftData(payloadData)) {
        populateProgramContainer(payloadString, programContainer);
        return true;
      }

      if (payloadData && typeof payloadData === "object") {
        programContainer.Entity = payloadData;
        DataUtils.assignFormValue(programContainer);
        return true;
      }

      return true;
    } catch (error) {
      console.warn("Unable to hydrate draft payload", error);
      return false;
    }
  };

  private readDraftPayloadFromIdStorage = (categoryId: string) => {
    const key = this.getDraftIdStorageKey(categoryId);
    if (!key) {
      return null;
    }
    const stored = sessionStorage.getItem(key);
    const parsed = this.tryParseDraftPayload(stored);
    const normalized = this.unwrapDraftPayload(parsed);
    if (!this.isDraftPayload(normalized)) {
      return null;
    }
    return typeof normalized === "string"
      ? normalized
      : CircularJSON.stringify(normalized);
  };

  private clearSelectedDraftIdFromUrl = () => {
    try {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has("draftId") && !currentUrl.searchParams.has("id")) {
        return;
      }

      currentUrl.searchParams.delete("draftId");
      currentUrl.searchParams.delete("id");
      const newSearch = currentUrl.search ? currentUrl.search : "";
      const newUrl = `${currentUrl.pathname}${newSearch}${currentUrl.hash ?? ""}`;
      window.history.replaceState({}, document.title, newUrl);
    } catch (error) {
      console.warn("Unable to clean draftId from URL", error);
    }
  };

  private findDraftRecordById = (drafts: any[], draftId: string) => {
    if (!Array.isArray(drafts) || !draftId) {
      return null;
    }
    const normalizedId = draftId.toString();
    const candidateKeys = ["id", "draftId", "DraftId"];
    return drafts.find((draft) =>
      candidateKeys.some((key) => {
        const value = draft?.[key];
        return value != null && value.toString() === normalizedId;
      }),
    );
  };

  private extractDraftIdFromRecord = (draftRecord: any): string | null => {
    if (!draftRecord || typeof draftRecord !== "object") {
      return null;
    }
    const candidateKeys = ["draftId", "DraftId", "DraftID", "id", "Id", "ID", "itemId"];
    for (const key of candidateKeys) {
      const value = draftRecord[key];
      if (value != null) {
        return value.toString();
      }
    }
    return null;
  };

  private resolveExistingDraftIdForUser = async (
    programContainer: ProgramContainer,
  ): Promise<string | null> => {
    const userId = DataUtils.resolveEmailCandidate(
      programContainer?.Profile?.email,
      programContainer?.Profile?.userId,
      (programContainer as any)?.UserId,
    );
    const token = SessionStorageService.getAccessToken(TOKEN_TYPE.API);

    if (!token || !programContainer?.CategoryId) {
      return null;
    }

    const metadata = await DataUtils.findExistingDraftMetadata(
      programContainer.CategoryId,
      userId,
      token,
    );

    if (!metadata?.draftId) {
      return null;
    }

    this.persistDraftId(metadata.draftId);
    DataUtils.persistDraftMetadata(programContainer.CategoryId, {
      ...metadata,
      formId: metadata.formId ?? programContainer.CategoryId,
      userId: metadata.userId ?? userId,
    });

    return metadata.draftId;
  };

  private applyResumeStepperIndex = () => {
    const resumeIndex = this.props.programContainer.resumeStepperIndex;
    if (
      typeof resumeIndex === "number" &&
      resumeIndex >= 0 &&
      resumeIndex !== this.state.stepperIndex
    ) {
      this.setState({ stepperIndex: resumeIndex }, () => {
        this.updateUrlForStep(resumeIndex);
      });
    }
  };

  private startPublicSecureSessionMonitor = () => {
    if (
      !this.requiresPublicSecureAuth() ||
      !this.hasAuthenticatedPublicSecureProfile() ||
      this.sessionExpiredRedirect
    ) {
      return;
    }

    if (this.sessionMonitor == null) {
      const intervalFromEnv = Number(
        getEnvVar('PUBLIC_SECURE_SESSION_CHECK_INTERVAL'),
      );
      const interval = Number.isFinite(intervalFromEnv)
        ? intervalFromEnv * 1000
        : 60000;
      this.sessionMonitor = window.setInterval(
        this.handlePublicSecureSessionCheck,
        interval,
      );
      window.addEventListener(
        "sessionStorageChange",
        this.handleSessionStorageChange as EventListener,
      );
    }

    this.handlePublicSecureSessionCheck();
  };

  private stopPublicSecureSessionMonitor = () => {
    if (this.sessionMonitor) {
      clearInterval(this.sessionMonitor);
      this.sessionMonitor = undefined;
    }

    window.removeEventListener(
      "sessionStorageChange",
      this.handleSessionStorageChange as EventListener,
    );
  };

  private startBpsSessionMonitor = () => {
    if (
      !this.requiresBpsAuth() ||
      !this.hasAuthenticatedBpsProfile() ||
      this.sessionExpiredRedirect
    ) {
      return;
    }

    if (this.bpsSessionMonitor == null) {
      const intervalFromEnv = Number(
        getEnvVar('PUBLIC_SECURE_SESSION_CHECK_INTERVAL'),
      );
      const interval = Number.isFinite(intervalFromEnv)
        ? intervalFromEnv * 1000
        : 60000;
      this.bpsSessionMonitor = window.setInterval(
        this.handleBpsSessionCheck,
        interval,
      );
    }

    this.handleBpsSessionCheck();
  };

  private stopBpsSessionMonitor = () => {
    if (this.bpsSessionMonitor) {
      clearInterval(this.bpsSessionMonitor);
      this.bpsSessionMonitor = undefined;
    }
  };

  private handleBpsSessionCheck = () => {
    if (
      !this.requiresBpsAuth() ||
      !this.hasAuthenticatedBpsProfile() ||
      this.sessionExpiredRedirect
    ) {
      return;
    }

    const token = getBpsAccessToken();

    if (!token) {
      this.stopBpsSessionMonitor();
      return;
    }

    if (!verifyBpsToken(token)) {
      this.sessionExpiredRedirect = true;
      this.stopBpsSessionMonitor();
      bpsClearSessionStorage();
      bpsSignin(
        this.state.programContainer ?? this.props.programContainer,
        this.isEnglish(this.state.programContainer?.Profile?.lang),
      );
    }
  };

  private handleSessionStorageChange = (
    event: Event | CustomEvent<{ name?: string }>,
  ) => {
    const customEvent = event as CustomEvent<{ name?: string }>;
    if (customEvent?.detail?.name === "authTokens") {
      this.handlePublicSecureSessionCheck();
    }
  };

  private handlePublicSecureSessionCheck = () => {
    if (
      !this.requiresPublicSecureAuth() ||
      !this.hasAuthenticatedPublicSecureProfile() ||
      this.sessionExpiredRedirect
    ) {
      return;
    }

    // Use getPSFormatToken (snake_case access_token) so we only test a genuine
    // PS Okta token.  cxp-ui's shareProfileInSession stores the CXP NextAuth
    // session in the same "authTokens" key using camelCase (accessToken).
    // valifyToken() fails on CXP tokens, wrongly triggering an Okta redirect
    // (→ 400).  By targeting only the PS-format token we avoid that false positive.
    const token = SessionStorageService.getPSFormatToken();

    if (!token) {
      // No real PS Okta token in session — user hasn't authenticated with PS yet
      // (e.g. navigating back to the form from cxp-ui via "Continue editing").
      // Stop the monitor and show the PS sign-in section so the user can
      // authenticate manually instead of being auto-redirected to Okta.
      this.stopPublicSecureSessionMonitor();
      const programContainer = this.state.programContainer ?? this.props.programContainer;
      this.showFirstPublicSection(programContainer);
      return;
    }

    if (!valifyToken(token)) {
      // Had a genuine PS token but it has expired — auto-redirect to re-authenticate.
      this.sessionExpiredRedirect = true;
      this.stopPublicSecureSessionMonitor();
      cleanup(
        this.props.programContainer.CategoryId,
        this.state.programContainer ?? this.props.programContainer,
      );
      signin(
        this.props.programContainer.CategoryId,
        this.isEnglish(this.state.programContainer?.Profile?.lang),
      );
    }
  };

  retrieveDraft = async () => {
    const draftMetadata = DataUtils.getDraftMetadataFromSession(
      this.props.programContainer?.CategoryId
    );
    if (draftMetadata?.draftId) {
      this.persistDraftId(draftMetadata.draftId.toString());
    }
    if (draftMetadata?.userId) {
      const existingProfile = this.props.programContainer.Profile || {};
      if (!existingProfile.userId) {
        existingProfile.userId = draftMetadata.userId;
      }
      if (!existingProfile.email) {
        existingProfile.email = draftMetadata.userId;
      }
      this.props.programContainer.Profile = existingProfile;
    }

    const hasSelectedDraft = !!this.getSelectedDraftId();

    if (hasSelectedDraft) {
      this.retrieveDraftFromAPI();
      return;
    }

    const loadedFromSession = this.retrieveDraftFromSession(this.props.programContainer);
    if (loadedFromSession) {
      return;
    }

    const storedDraftId = this.props.programContainer?.CategoryId
      ? this.readDraftIdFromStorage(this.props.programContainer.CategoryId)
      : null;
    if (storedDraftId) {
      // Do not call signin() here. The user may be arriving from cxp-ui
      // ("Continue editing") with a CXP session but no PS Okta token yet.
      // Calling signin() with the stored draft's appId builds a redirect_uri
      // that is not registered in Okta (→ 400). Draft retrieval does not
      // require PS authentication; resolveProfile / handlePublicSecureSessionCheck
      // handle the auth flow independently.
      this.retrieveDraftFromAPI(storedDraftId);
      return;
    }

    const existingDraftId = await this.resolveExistingDraftIdForUser(
      this.props.programContainer,
    );
    if (existingDraftId) {
      this.retrieveDraftFromAPI(existingDraftId);
    }

    // If no selection and nothing cached, stay on a fresh form.
  };

  retrieveDraftFromAPI = async (draftIdOverride?: string) => {
    //  if (this.props.programContainer.Profile?.email) {
      // const categoryId = this.props.programContainer.CategoryId;
      const selectedDraftId = draftIdOverride || this.getSelectedDraftId();
      const baseUrl = getEnvVar('LAYOUT_API_URL') + "/data/admin/drafts/id"

      const url = selectedDraftId
        ? `${baseUrl}/${encodeURIComponent(selectedDraftId)}`
        : baseUrl;

      axios.get(url).then(async (response) => {
        const payload = response.data;

        const draftRecord = Array.isArray(payload)
          ? selectedDraftId
            ? this.findDraftRecordById(payload, selectedDraftId) ?? payload[0]
            : payload[0]
          : payload;

        if (!draftRecord?.formValue) {
          console.warn(
            "Unable to locate draft data with formValue",
            selectedDraftId,
          );
          return;
        }
        
        const rawFormValue = draftRecord.formValue;
        const hydrated = this.tryHydrateDraftPayload(
          rawFormValue,
          this.props.programContainer,
        );
        if (!hydrated) {
          const parsedFormValue =
            typeof rawFormValue === "string"
              ? this.tryParseDraftPayload(rawFormValue)
              : rawFormValue;
          if (parsedFormValue == null) {
            console.warn("Unable to parse draft formValue payload");
            return;
          }
          this.props.programContainer.Entity = parsedFormValue;
          DataUtils.assignFormValue(this.props.programContainer);
        }
        
        const resolvedDraftId =
          selectedDraftId || this.extractDraftIdFromRecord(draftRecord);
        if (resolvedDraftId) {
          const resolvedDraftIdString = resolvedDraftId.toString();
          this.persistDraftId(resolvedDraftIdString);
          const metadataForPersist = {
            id:
              draftRecord?.id != null
                ? draftRecord.id.toString()
                : resolvedDraftIdString,
            draftId: resolvedDraftIdString,
            userId: DataUtils.resolveEmailCandidate(
              draftRecord?.userId,
              draftRecord?.user_id,
              this.props.programContainer.Profile?.email,
              this.props.programContainer.Profile?.userId,
            ),
            formId:
              draftRecord?.formId ??
              draftRecord?.form_id ??
              this.props.programContainer.CategoryId,
          };
          DataUtils.persistDraftMetadata(
            this.props.programContainer.CategoryId,
            metadataForPersist,
          );
        }
        this.applyResumeStepperIndex();
        this.clearSelectedDraftIdFromUrl();
      });
    // }
  };

  retrieveDraftFromSession = (
    programContainer: ProgramContainer,
    purgeOnly = false,
  ): boolean => {
    const cachedData = sessionStorage.getItem(
      `draft_${programContainer.CategoryId}`,
    );
    const fallbackData = cachedData
      ? null
      : this.readDraftPayloadFromIdStorage(programContainer.CategoryId);
    const payload = cachedData ?? fallbackData;

    if (payload) {
      if (purgeOnly) {
        if (cachedData) {
          sessionStorage.removeItem(`draft_${programContainer.CategoryId}`);
        } else if (fallbackData) {
          sessionStorage.removeItem(
            `draftId_${programContainer.CategoryId}`,
          );
        }
        programContainer.resumeStepperIndex = 0;
        console.log("Session draft cleared for new start:", programContainer.CategoryId);
        return false;
      }

      const hydrated = this.tryHydrateDraftPayload(payload, programContainer);
      if (!hydrated) {
        try {
          programContainer.Entity = CircularJSON.parse(payload);
          DataUtils.assignFormValue(programContainer);
        } catch (error) {
          console.warn("Unable to parse session draft payload", error);
          return false;
        }
      }

      const storedDraftId = programContainer?.CategoryId
        ? this.readDraftIdFromStorage(programContainer.CategoryId)
        : null;
      if (storedDraftId) {
        this.persistDraftId(storedDraftId);
      }
      this.applyResumeStepperIndex();
      console.log("Draft data retrieved from session storage:", payload);
      return true;
    }
    return false;
  };

  private async resumePendingDraft(programContainer: ProgramContainer) {
    const pendingAppId = sessionStorage.getItem("pendingDraftAppId");
    if (
      !pendingAppId ||
      !programContainer?.CategoryId ||
      pendingAppId.toLowerCase() !== programContainer.CategoryId.toLowerCase()
    ) {
      return;
    }

    // Remove the flag immediately to prevent duplicate saves if this method
    // fires more than once before the async save completes.
    sessionStorage.removeItem("pendingDraftAppId");

    const shouldRedirectToDrafts =
      sessionStorage.getItem("redirectToDrafts") === "true";

    const loaded = this.retrieveDraftFromSession(programContainer);
    if (loaded && programContainer.Profile?.email) {
      try {
        await DataUtils.saveDraft(programContainer);
      } finally {
        if (shouldRedirectToDrafts) {
          sessionStorage.removeItem("redirectToDrafts");
          window.location.replace(
            `${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/drafts`,
          );
          return;
        }
      }
    }
  }

  htmlString = () => {
    let htmlString = [];
    if (
      getEnvVar('DYNAMIC_SCRIPT_ENABLED') === "true" &&
      getEnvVar('DYNAMIC_SCRIPT_NAME')
    ) {
      htmlString.push(
        <DynamicScriptContent
          scripts={Scripts[getEnvVar('DYNAMIC_SCRIPT_NAME')]}
        />,
      );
    }

    if (this.props.programContainer.HeaderType == "Ontario") {
      if (this.props.programContainer.AuthRequired == "ad") {
        htmlString.push(
          <div key="runtime_component_msal_provider">
            <RuntimeHeaderComponent
              // programContainer={this.props.programContainer}
              programContainer={this.state.programContainer}
              isEng={this.state.isEng}
              headerConfig={this.state.programContainer.HeaderConfig}
              comments={this.state.programContainer.Entity?.comments ?? []}
              profile={this.props.programContainer.Profile}
              callback={(isEng) => {
                const effectiveIsEng = this.props.programContainer.EnglishOnly || isEng;
                document.documentElement.lang = effectiveIsEng ? "en" : "fr";
                this.setState({ isEng: effectiveIsEng });
              }}
              callbackState={(programContainer) => {
                SectionUtils.hydrateParentReferences(programContainer);
                this.setState({ programContainer: programContainer });
              }}
              callbackCMASaved={() => this.displayToast()}
              callbackLogin={() => {
                let token = SessionStorageService.getAccessToken(
                  TOKEN_TYPE.API,
                );
                if (
                  token &&
                  isRuntime() !== "deployed" &&
                  this.state.programContainer?.APIBasePath?.indexOf(
                    "on-form-service",
                  ) > 0
                ) {
                  const categoryId = getAppNameFromPath();
                  const url = "data/case/get/" + categoryId;
                  const urlFull = URLUtils.concatUrls(url);
                  const headers = {
                    Authorization: `Bearer ${token}`,
                  };

                  axios
                    .get(urlFull, { headers })
                    .then((response) => {
                      const parsedProgramContainer = CircularJSON.parse(
                        response.data.attachment,
                      ) as ProgramContainer;
                      //populateElementFindRoot(parsedProgramContainer);

                      this.setState({
                        programContainer: parsedProgramContainer,
                      });

                      sessionStorage.removeItem("preAuthUrl");
                      localStorage.removeItem("preAuthUrl");
                    })
                    .catch(async (error) => {
                      if (error?.response?.status === 401) {
                        console.warn("401 received");

                        if (this.props.programContainer.AuthRequired === "ad") {
                          try {
                            await handleAzure401();
                            console.info("Azure re-auth succeeded");
                            // Optionally retry the request here
                            return;
                          } catch (e) {
                            console.error("Azure re-auth failed", e);
                            return;
                          }
                        }

                        // Public Secure / other auth flows
                        cleanup(
                          this.props.programContainer.CategoryId,
                          this.state.programContainer ?? this.props.programContainer,
                        );

                        signin(
                          this.props.programContainer.CategoryId,
                          this.isEnglish(this.state.programContainer?.Profile?.lang),
                        );
                      }
                    });

                }

                let profile = {
                  Email: SessionStorageService.getUserId(),
                };
                this.props.programContainer.Profile = profile;
              }}
            />
          </div>,
        );
      } else {
        htmlString.push(
          <RuntimeHeaderComponent
            programContainer={this.props.programContainer}
            isEng={this.state.isEng}
            callback={(isEng) => {
              const effectiveIsEng = this.props.programContainer.EnglishOnly || isEng;
              document.documentElement.lang = effectiveIsEng ? "en" : "fr";
              this.setState({ isEng: effectiveIsEng });
            }}
            callbackCMASaved={() => this.displayToast()}
          />,
        );
      }
    }

    htmlString.push(
      <div key="runtime_component_main_div_parent">
        {this.props.programContainer.HeaderType == "ServiceOntario" && (
          <div>
            <ServiceOntarioHeaderComponent
              programContainer={this.props.programContainer}
              isEng={this.state.isEng ?? true}
              profile={this.props.programContainer.Profile}
              callbackProgramContainer={(programContainer) => {
                this.setState({ programContainer: programContainer });
              }}
              callback={(isEng) => {
                this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng });
              }}
            />
          </div>
        )}

        {this.props.programContainer.HeaderType == "Application" && (
          <div>
            <ApplicationHeaderComponent
              programContainer={this.props.programContainer}
              isEng={this.state.isEng ?? true}
              callback={(isEng) => {
                this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng });
              }}
              callbackStepIndex={(index) => {
                this.setState({ stepperIndex: index }, () => this.updateUrlForStep(index));
              }}
            ></ApplicationHeaderComponent>
          </div>
        )}

        {this.props.programContainer.HeaderType == "Labour" && (
          <div>
            <LabourHeaderComponent
              programContainer={this.props.programContainer}
              isEng={this.state.isEng ?? true}
              profile={this.props.programContainer.Profile}
              initialLandingURL={
                this.props.programContainer.DynamicOriginWithPublicSecure === 'Y' &&
                this.props.programContainer.AuthRequired === 'ps'
                  ? this.initialLandingURL
                  : undefined
              }
              // onAllowExit={this.allowExit}
              callbackProgramContainer={(programContainer) => {
                this.setState({ programContainer: programContainer });
              }}
              callback={(isEng) => {
                this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng });
              }}
              callbackStepIndex= {(currenPageIndex: number) =>
                this.setState(prev => ({stepperIndex: currenPageIndex,headerClickNonce: prev.headerClickNonce + 1}))}
            />
          </div>
        )}

        {this.props.programContainer.HeaderType == "MCCSS" && (
          <div>
            <MCCSSHeaderComponent
              programContainer={this.props.programContainer}
              isEng={this.state.isEng ?? true}
              callbackProgramContainer={(programContainer) => {
                this.setState({ programContainer: programContainer });
              }}
              callback={(isEng) => {
                this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng });
              }}
            />
          </div>
        )}

        <div
          style={{
            display: this.state.showSessionEndPage ? "block" : "none",
            maxWidth: "72rem",
            margin: "auto",
            padding: "0 1rem",
          }}
        >
          <SessionEndComponent
            isEng={this.state.isEng}
            programContainer={this.props.programContainer}
          />
        </div>
        <main id="main-content">
          {/* <HelmetComponent title='dynamic title' /> */}
          <div
            style={{
              display: this.state.showSessionEndPage ? "none" : "block",
            }}
          >
            {this.state.isInitializingClientScript ? (
              <Processing />
            ) : (
              <>
                {!!this.props.programContainer.PageRedirection?.shouldHandle && (
                  <RuntimeWithPageRedirect
                    key="runtime-with-page-redirect"
                    isEng={this.state.isEng ?? true}
                    programContainer={this.props.programContainer}
                    stepperIndex={this.state.stepperIndex}
                    callbackStepperIndex={(stepperIndex) =>
                      this.setState({ stepperIndex })
                    }
                  />
                )}

                {!this.props.programContainer.PageRedirection?.shouldHandle && (
                  <FormPreviewComponent
                    key={`Fixed-key-for-FormPreviewComponent`}
                    programContainer={this.state.programContainer}
                    isProfileLoaded={this.state.isProfileLoaded}
                    isEng={
                      (this.state.isEng ?? true) ||
                      this.props.programContainer.EnglishOnly
                    }
                    stepperIndex={this.state.stepperIndex}
                    onStepChange={(stepperIndex) => {
                      this.setState({ stepperIndex }, () => {
                        this.updateUrlForStep(stepperIndex);
                      });
                    }}
                    callBackLang={(isEng) => this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng })}
                    callback={(programContainer) => {
                      this.setState({ programContainer: programContainer})}}
                    callbackOriginalData={(originalData) => {
                      console.log(originalData);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </main>
        <RuntimeFooterComponent
          programContainer={this.state.programContainer}
          callbackStepIndex={(stepperIndex) => {
            this.setState({ stepperIndex }, () => {
              this.updateUrlForStep(stepperIndex);
            });
          }}
          isEng={this.state.isEng || this.props.programContainer.EnglishOnly}
        ></RuntimeFooterComponent>
      </div>,
    );
    htmlString.push(
      <div key="runtime_component_toast_div_parent">
        {/* @ts-ignore */}
        <Toast ref={(el) => (this.growl = el)} />
      </div>,
    );
    return htmlString;
  };

  displayToast = () => {
    this.growl.show({
      severity: "success",
      summary: "saved",
      detail: "Data saved",
    });
  };
  render() {
    if (this.props.programContainer == null) return null;

    // if (this.state.showModal) {
    //     this.events.forEach(event => window.removeEventListener(event, this.handleEvent));
    //     if (this.idleTimeout) clearTimeout(this.idleTimeout);
    // }
    if (this.state.programContainer?.adminConfig?.isActive) {
      return (
        <main id="main-content" key="runtime_component_main_content">
          <div
            style={{
              display: "block",
              maxWidth: "72rem",
              margin: "auto",
              padding: "0px 1rem",
            }}
          >
            <Maintenance
              adminConfig={this.props.programContainer.adminConfig}
              app={
                this.state.isEng
                  ? this.props.programContainer.Name
                  : this.props.programContainer.NameFRA
              }
              lang={this.state.isEng ? "en" : "fr"}
            />
          </div>
        </main>
      );
    }

    return this.htmlString();
  }

   private isEnglish = (lang?: string) => lang ? lang !== "fr" : (this.state.isEng ?? true);

  private async populateMessages(
    programContainer: ProgramContainer,
  ): Promise<void> {
    if (!programContainer.Profile?.userId || !programContainer.Profile?.token) {
      return;
    }

    const messages = await getCxpMessages(programContainer.Profile.token, {
      pageNumber: 1,
      pageSize: 20,
      search: "",
    });
    programContainer.Profile.messages = messages?.messages;
  }

  private showFirstPublicSection(programContainer: ProgramContainer) {
    const section = getPublicSecurePage(programContainer.ListOfFormSections);
    if (section) {
      section.Visible = true;
    }
  }

  private async runPublicSecurePreScript(
    programContainer: ProgramContainer,
  ): Promise<void> {
    if (this.publicSecurePreScriptRun) {
      return;
    }

    const publicSecureSection = getPublicSecurePage(
      programContainer.ListOfFormSections,
    );
    if (!publicSecureSection) {
      return;
    }

    const sectionWithFlags = publicSecureSection as FormSections & {
      __preScriptExecuted?: boolean;
    };
    if (sectionWithFlags.__preScriptExecuted) {
      this.publicSecurePreScriptRun = true;
      return;
    }

    const preScript = publicSecureSection.PreScript;
    if (typeof preScript !== "string" || preScript.trim().length === 0) {
      return;
    }

    this.publicSecurePreScriptRun = true;
    sectionWithFlags.__preScriptExecuted = true;
    try {
      const customJSFunc = eval(`(${preScript})`);
      if (typeof customJSFunc === "function") {
        await customJSFunc(publicSecureSection, ENV);
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  private normalizeHeaderInitScript(script?: string): string {
    if (!script) {
      return '';
    }

    const withBreaks = script
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr)>/gi, '\n');
    const stripped = withBreaks.replace(/<[^>]+>/g, '');
    return stripped
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .trim();
  }

  private runHeaderInitScript(programContainer: ProgramContainer): void {
    if (this.headerInitScriptRun) {
      return;
    }
    this.headerInitScriptRun = true;

    const rawScript = programContainer.HeaderInitScript;
    const script = this.normalizeHeaderInitScript(rawScript);
    if (!script) {
      return;
    }

    try {
      const customJSFunc = eval(`(${script})`);
      if (typeof customJSFunc === 'function') {
        customJSFunc(programContainer, ENV);
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  private triggerSignin(programContainer: ProgramContainer) {
    const preferEnglish =
      programContainer.Profile?.lang != null
        ? this.isEnglish(programContainer.Profile.lang)
        : (this.state.isEng ?? true);

    console.log("no token, redirect to signin");
    signin(programContainer.CategoryId, preferEnglish);
  }

  private triggerBpsSignin(programContainer: ProgramContainer) {
    const preferEnglish =
      programContainer.Profile?.lang != null
        ? this.isEnglish(programContainer.Profile.lang)
        : (this.state.isEng ?? true);

    console.log("no BPS token, redirect to BPS signin");
    bpsSignin(programContainer, preferEnglish);
  }

  private async runInitialDataApi(programContainer: ProgramContainer): Promise<void> {
    if (this.initialDataApiRun) {
      return;
    }

    const initialDataApi = programContainer.InitialDataAPI;
    const token = programContainer.Profile?.token;

    if (!initialDataApi || !token) {
      return;
    }

    this.initialDataApiRun = true;
    try {
      const apiKey = programContainer.ApiKeyKey && programContainer.ApiKeyValue
        ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
        : null;

      const headers = setBaseHeaders("application/json", token, apiKey);
      const url = combineUrl(programContainer.APIBasePath || "", initialDataApi);

      const response = await axios.get(url, {
        headers,
        withCredentials: true,
      });

      const payload = response?.data?.WebResponse ?? response?.data;
      if (payload) {
        programContainer.Entity = payload;
        DataUtils.assignFormValue(programContainer);
      }
    } catch (error) {
      console.error("InitialDataAPI error", error);
    }
  }

  private findPublicSecureButton(section?: FormSections): FormSectionElements | null {
    if (!section) {
      return null;
    }

    for (const subSection of section.ListOfFormSubSections || []) {
      for (const element of subSection.ListOfFormSectionElements || []) {
        if (element.Datatype === "PublicSecureButton") {
          return element;
        }
      }
    }

    return null;
  }

  private runElementScripts(element: FormSectionElements): void {
    if (!element || element.Visible === false) {
      return;
    }

    if (element.Datatype === "Row" && element.subSectionList?.length) {
      element.subSectionList.forEach((subSection) => {
        subSection.ListOfFormSectionElements?.forEach((child) => {
          this.runElementScripts(child);
        });
      });
      return;
    }

    FormValidationUtils.runJavascript(element);
  }

  private runSectionElementScripts(section?: FormSections): void {
    if (!section) {
      return;
    }

    section.ListOfFormSubSections?.forEach((subSection) => {
      subSection.ListOfFormSectionElements?.forEach((element) => {
        this.runElementScripts(element);
      });
    });
  }

  private resolveStepperIndexFromScript(
    programContainer: ProgramContainer,
    destination: string | number,
  ): number | undefined {
    if (typeof destination === "string") {
      const match = programContainer.ListOfFormSections.find(
        (section) =>
          section.ENUTitle === destination || section.FRATitle === destination,
      );
      if (!match) {
        return undefined;
      }
      return programContainer.ListOfFormSections.indexOf(match);
    }

    if (typeof destination === "number" && Number.isFinite(destination)) {
      return destination;
    }

    return undefined;
  }

  private async runPublicSecureSectionScripts(
    programContainer: ProgramContainer,
  ): Promise<void> {
    const publicSecureSection = getPublicSecurePage(
      programContainer.ListOfFormSections,
    );
    if (!publicSecureSection) {
      return;
    }

    const publicSecureButton = this.findPublicSecureButton(publicSecureSection);

    const stepperScript = publicSecureButton?.StepperScript || publicSecureSection.StepperScript;
    if (typeof stepperScript === "string" && stepperScript.trim().length > 0) {
      try {
        const customJSFunc = eval(`(${stepperScript})`);
        if (typeof customJSFunc === "function") {
          await customJSFunc((destination: string | number) => {
            const index = this.resolveStepperIndexFromScript(
              programContainer,
              destination,
            );
            if (index == null) {
              return;
            }
            this.setState({ stepperIndex: index }, () => {
              this.updateUrlForStep(index);
            });
          });
        }
      } catch (ex) {
        console.log(ex);
      }
    }

    this.runSectionElementScripts(publicSecureSection);

    const postScript = publicSecureSection.PostScript;
    if (typeof postScript === "string" && postScript.trim().length > 0) {
      try {
        const customJSFunc = eval(`(${postScript})`);
        if (typeof customJSFunc === "function") {
          await customJSFunc(publicSecureSection, ENV);
        }
      } catch (ex) {
        console.log(ex);
      }
    }
  }

  private async handleAuthorizationCallback(
    programContainer: ProgramContainer,
  ): Promise<ProgramContainer> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const queryState = url.searchParams.get("state");

    console.log('[handleAuthorizationCallback] Called with code:', !!code, 'state:', !!queryState);
    console.log('[handleAuthorizationCallback] Has existing token:', !!programContainer.Profile?.token);

    if (!code || programContainer.Profile?.token) {
      console.log('[handleAuthorizationCallback] Exiting early - no code or already has token');
      return programContainer;
    }

    // Check if this is a BPS callback (BPS config exists or auth method is bps)
    const isBpsAuth = programContainer.AuthRequired?.toLowerCase() === 'bps' ||
      getBpsConfig(programContainer) !== null ||
      sessionStorage.getItem('bps_codeVerifier') !== null;

    if (isBpsAuth) {
      console.log('[handleAuthorizationCallback] Handling as BPS auth');
      return this.handleBpsAuthorizationCallback(programContainer, queryState || "");
    }

    // For non-BPS auth, state is required
    if (!queryState) {
      console.log('[handleAuthorizationCallback] No state parameter, exiting');
      return programContainer;
    }

    // Handle Public Secure (Okta) callback
    try {
      console.log("[handleAuthorizationCallback] get token and cxp profile");
      const authToken = sessionStorage.getItem("authTokens");
      const token = authToken ? JSON.parse(authToken) : null;

      const updatedContainer = await checkProfileAccount(
        programContainer,
        token,
      );
      
      console.log('[handleAuthorizationCallback] After checkProfileAccount, has token:', !!updatedContainer.Profile?.token);
      console.log('[handleAuthorizationCallback] After checkProfileAccount, has userId:', !!updatedContainer.Profile?.userId);
      
      const profile = updatedContainer.Profile;
      if (profile) {
        console.log("after get token and cxp profile", profile);
        if (queryState.endsWith("FR")) {
          profile.lang = "fr";
        } else if (queryState.endsWith("EN")) {
          profile.lang = "en";
        }
      }

      if (!updatedContainer?.Profile?.token) {
        console.log("fetch token failure, re-signin");
      } else {
        if (updatedContainer.InitialDataAPI) {
          await this.runInitialDataApi(updatedContainer);
          await this.runPublicSecureSectionScripts(updatedContainer);
        }
        // Clean up the auth code/state from the URL after successful exchange (Public Secure / Okta)
        this.cleanAuthCodeFromUrl();
      }

      return updatedContainer;
    } catch (error) {
      console.log("error get token and cxp profile", error);
      return programContainer;
    }
  }

  private async handleBpsAuthorizationCallback(
    programContainer: ProgramContainer,
    queryState: string,
  ): Promise<ProgramContainer> {
    try {
      console.log("BPS: exchanging code for tokens");

      const updatedContainer = await bpsCheckProfileAccount(programContainer);
      const profile = updatedContainer.Profile;

      if (profile) {
        console.log("BPS: tokens received, profile updated", profile);
        if (queryState.endsWith("FR")) {
          profile.lang = "fr";
        } else if (queryState.endsWith("EN")) {
          profile.lang = "en";
        }
      }

      if (!updatedContainer?.Profile?.token) {
        console.log("BPS: fetch token failure");
      } else {
        // Clean up URL after successful token exchange to prevent re-using code
        this.cleanBpsCodeFromUrl();
      }

      return updatedContainer;
    } catch (error) {
      console.log("BPS: error during token exchange", error);
      return programContainer;
    }
  }

  private cleanBpsCodeFromUrl(): void {
    try {
      // For BPS: restore the original URL with appId from preAuthUrl
      const preAuthUrl = sessionStorage.getItem("preAuthUrl") || localStorage.getItem("preAuthUrl");
      if (preAuthUrl) {
        const originalUrl = new URL(preAuthUrl);
        window.history.replaceState({}, document.title, originalUrl.pathname + originalUrl.search + (originalUrl.hash ?? ""));
        console.log("BPS: restored original URL:", originalUrl.pathname);
        return;
      }

      // Fallback: just remove the code from current URL
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has("code")) {
        return;
      }
      currentUrl.searchParams.delete("code");
      currentUrl.searchParams.delete("state");
      const newSearch = currentUrl.search ? currentUrl.search : "";
      const newUrl = `${currentUrl.pathname}${newSearch}${currentUrl.hash ?? ""}`;
      window.history.replaceState({}, document.title, newUrl);
      console.log("BPS: cleaned code from URL");
    } catch (error) {
      console.warn("Unable to clean BPS code from URL", error);
    }
  }

  private cleanAuthCodeFromUrl(): void {
    try {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has("code") && !currentUrl.searchParams.has("state")) {
        return;
      }
      currentUrl.searchParams.delete("code");
      currentUrl.searchParams.delete("state");
      const newSearch = currentUrl.search ? currentUrl.search : "";
      const newUrl = `${currentUrl.pathname}${newSearch}${currentUrl.hash ?? ""}`;
      window.history.replaceState({}, document.title, newUrl);
      console.log("Auth: cleaned code/state from URL");
    } catch (error) {
      console.warn("Unable to clean auth code/state from URL", error);
    }
  }

  private async resolveProfile(
    programContainer: ProgramContainer,
  ): Promise<ProgramContainer> {
    let container = programContainer;

    // Handle BPS authentication separately
    if (container.AuthRequired?.toLowerCase() === 'bps') {
      return this.resolveBpsProfile(container);
    }

    // Handle Public Secure and other auth methods
    const profileFromSession = getUserProfileFromSession();
    // Hydrate only with a real PS Okta token (snake_case access_token) — NOT with
    // the CXP NextAuth accessToken (camelCase) that shareProfileInSession stores in
    // authTokens.  Treating a CXP token as a PS token causes valifyToken() to fail,
    // which triggers a spurious Okta redirect (→ 400) in handlePublicSecureSessionCheck.
    const hydratedProfileFromSession = SessionStorageService.hydrateProfileWithPSFormatToken(profileFromSession);
    container.Profile = hydratedProfileFromSession;

    if (!hydratedProfileFromSession?.token && hydratedProfileFromSession?.userId) {
      container = await fetchSessionFromServer(container);
      console.log("fetch token form auth session: ", container.Profile);
    }

    container = await this.handleAuthorizationCallback(container);

    const profile = container.Profile;

    if (profile?.userId && !profile?.token && container.AuthRequired=="ps") {
      await this.runPublicSecurePreScript(container);
      // User has CXP credentials but no PS token (e.g. navigating from draft detail page).
      // Show the PS section so the user can initiate authentication manually
      // instead of auto-redirecting to Okta, which can cause a 400 if the session
      // state is stale or the redirect_uri is not yet registered for this context.
      this.showFirstPublicSection(container);
    }

    if (profile?.userId && profile?.token) {
      await this.populateMessages(container);
    } else if (!profile) {
      this.showFirstPublicSection(container);
      await this.runPublicSecurePreScript(container);
    }

    return container;
  }

  private async resolveBpsProfile(
    programContainer: ProgramContainer,
  ): Promise<ProgramContainer> {
    let container = programContainer;
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");

    console.log("BPS resolveBpsProfile called:", {
      hasCode: !!code,
      hasBpsConfig: !!container.BpsConfig,
      authRequired: container.AuthRequired
    });

    // Check for existing valid BPS token
    const existingToken = getBpsAccessToken();
    if (existingToken && verifyBpsToken(existingToken)) {
      console.log("BPS: using existing valid token");
      // Populate profile from cached data if needed
      const cachedProfile = sessionStorage.getItem('bps_userProfile');
      if (cachedProfile) {
        try {
          container.Profile = {
            ...container.Profile,
            ...JSON.parse(cachedProfile),
            token: existingToken
          };
        } catch {
          // Ignore parse errors
        }
      }
      return container;
    }

    // Handle callback with authorization code
    if (code) {
      console.log("BPS: handling authorization callback with code");
      container = await this.handleBpsAuthorizationCallback(
        container,
        url.searchParams.get("state") || ""
      );

      if (container.Profile?.token) {
        console.log("BPS: token exchange successful");
        return container;
      } else {
        console.log("BPS: token exchange failed, profile.token is missing");
      }
    }

    // No valid token and no code - need to initiate login
    const bpsConfig = getBpsConfig(container);
    if (bpsConfig) {
      console.log("BPS: no valid token, initiating login");
      this.triggerBpsSignin(container);
    } else {
      console.error("BPS: authentication required but BpsConfig is missing");
    }

    return container;
  }

  getProfile = async (programContainer: ProgramContainer) => {
    console.log('[getProfile] Starting getProfile');
    console.log('[getProfile] Has Profile token:', !!programContainer.Profile?.token);
    console.log('[getProfile] Has Profile userId:', !!programContainer.Profile?.userId);
    
    if (this.initialClientScriptPromise) {
      await this.initialClientScriptPromise;
      this.initialClientScriptPromise = undefined;
    }

    let container = await this.resolveProfile(programContainer);
    console.log('[getProfile] After resolveProfile, has token:', !!container.Profile?.token);
    console.log('[getProfile] After resolveProfile, has userId:', !!container.Profile?.userId);
    
    container.adminConfig = await getAdminConfig(container.CategoryId);
    if (!this.hasDraftToRestore(container)) {
      await this.runInitialDataApi(container);
    }
    this.runHeaderInitScript(container);

    console.log('[getProfile] About to setState and trigger advancement check');
    
    // Determine if we should keep tracking paused (user should skip sections) or resume it
    // Skip sections if user is signed in from CXP (has token/userId)
    const isSignedInFromCxp = !!(
      container.Profile?.token || 
      container.Profile?.userId || 
      sessionStorage.getItem("userProfile")
    );
    
    if (isSignedInFromCxp) {
      console.log('[getProfile] User is signed in from CXP - keeping tracking paused during section skip');
      // Keep gaUploadPause=true (already set in componentDidMount if user exists)
    } else {
      console.log('[getProfile] User not signed in from CXP - resuming tracking');
      sessionStorage.removeItem("gaUploadPause");
    }
    
    this.setState({
      isEng: this.isEnglish(container.Profile?.lang),
      programContainer: container,
      isInitializingClientScript: false,
      isProfileLoaded: true,
    }, () => {
      console.log('[getProfile] setState callback executing');
      this.resumePendingDraft(container);
      const didAdvanceAfterCxpReturn = this.advanceAfterCxpAccountCreation(container);
      if (!didAdvanceAfterCxpReturn) {
        this.skipToValidSectionOnInitialLoad(container);
      }
    });
  };

  private cleanupStaleAdvanceFlag(): void {
    // Check if the flag exists and is expired, then remove it
    try {
      const stored = localStorage.getItem('advanceAfterCxpReturn');
      if (!stored) {
        return;
      }
      
      const flagData = JSON.parse(stored);
      if (flagData && flagData.timestamp && flagData.expiresIn) {
        const now = Date.now();
        const expirationTime = flagData.timestamp + flagData.expiresIn;
        
        if (now >= expirationTime) {
          localStorage.removeItem('advanceAfterCxpReturn');
          console.log('Expired advanceAfterCxpReturn flag removed on mount');
        }
      }
    } catch {
      // Ignore errors
    }
  }

  private advanceAfterCxpAccountCreation(programContainer: ProgramContainer): boolean {
    console.log('[advanceAfterCxpAccountCreation] Called');
    console.log('[advanceAfterCxpAccountCreation] Current stepperIndex:', this.state.stepperIndex);
    console.log('[advanceAfterCxpAccountCreation] Total sections:', programContainer.ListOfFormSections?.length);

    // Check if we should advance past the public secure section after CXP account creation
    // Using localStorage because it persists across domain navigations (unlike sessionStorage)
    const shouldAdvance = getAndValidateAdvanceFlag();
    console.log('[advanceAfterCxpAccountCreation] shouldAdvance:', shouldAdvance);
    if (!shouldAdvance) {
      console.log('[advanceAfterCxpAccountCreation] Not advancing - flag returned false');
      return false;
    }

    // Find the public secure section
    const publicSecureSection = getPublicSecurePage(programContainer.ListOfFormSections);
    console.log('[advanceAfterCxpAccountCreation] publicSecureSection found:', !!publicSecureSection);
    if (!publicSecureSection) {
      console.log('[advanceAfterCxpAccountCreation] Not advancing - no public secure section');
      return false;
    }

    // Find its index in the list
    const publicSecureIndex = programContainer.ListOfFormSections.findIndex(
      section => section === publicSecureSection
    );
    console.log('[advanceAfterCxpAccountCreation] publicSecureIndex:', publicSecureIndex);

    if (publicSecureIndex < 0) {
      console.log('[advanceAfterCxpAccountCreation] Not advancing - public secure index is negative');
      return false;
    }

    const authReturnSource = localStorage.getItem('authReturnSource');
    const shouldTrackPublicSecureAfterSignin =
      localStorage.getItem('trackPublicSecureAfterSignin') === 'true' &&
      authReturnSource === 'publicSecureButton';

    // For button-initiated sign-in, keep Public Secure section as previousUrl
    // so the next visible section can use it as referrer context.
    if (
      authReturnSource === 'publicSecureButton' &&
      publicSecureSection?.Visible !== false
    ) {
      const previousUrlFromButtonFlow =
        localStorage.getItem('previousUrlBeforeOAuth') || window.location.href;
      sessionStorage.setItem('previousUrl', previousUrlFromButtonFlow);
    }

    if (
      localStorage.getItem('trackPublicSecureAfterSignin') === 'true' &&
      authReturnSource !== 'publicSecureButton'
    ) {
      localStorage.removeItem('trackPublicSecureAfterSignin');
    }

    if (shouldTrackPublicSecureAfterSignin) {
      const wasPaused = sessionStorage.getItem("gaUploadPause") === "true";
      if (wasPaused) {
        sessionStorage.removeItem("gaUploadPause");
      }
      TrackPageView(programContainer);
      if (wasPaused) {
        sessionStorage.setItem("gaUploadPause", "true");
      }
    }

    // Find the next visible section after public secure, skipping sections that should be bypassed
    let targetIndex = publicSecureIndex + 1;
    console.log('[advanceAfterCxpAccountCreation] Initial targetIndex:', targetIndex);
    const hasAcceptedTerms = !!(
      programContainer.Profile?.agreedToTermsOfUseOn ||
      (programContainer as any)?.Entity?.accepted === true
    );
    
    
    const isSignedInFromCxp = programContainer.Profile?.token || 
    programContainer.Profile?.userId || 
    sessionStorage.getItem("userProfile");
    if(!isSignedInFromCxp && programContainer.AuthRequired === 'ps') {
      console.log('[advanceAfterCxpAccountCreation] Not advancing - user is not signed in after returning from CXP');
      return false;
    }
    
    // Skip hidden sections and sections that should be bypassed based on user state
    while (targetIndex < programContainer.ListOfFormSections.length) {
      const section = programContainer.ListOfFormSections[targetIndex];
      
      // Skip if section is hidden
      if (section?.Visible === false) {
        console.log('[advanceAfterCxpAccountCreation] Section', targetIndex, 'is hidden, skipping');
        targetIndex++;
        continue;
      }
      
      const sectionSlug = (section?.pageName || section?.ENUTitle || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      // Skip onboarding instructions always, and skip other SkipPageViewTracking
      // sections only when user already accepted Terms of Use.
      if (sectionSlug === "instructions" || (section?.SkipPageViewTracking && hasAcceptedTerms)) {
        console.log('[advanceAfterCxpAccountCreation] Section', targetIndex, 
                   `("${section.ENUTitle}") skipped by onboarding rules; hasAcceptedTerms=${hasAcceptedTerms}`);
        targetIndex++;
        continue;
      }
      
      // Found a valid section to navigate to
      break;
    }
    
    console.log('[advanceAfterCxpAccountCreation] Final targetIndex:', targetIndex);
    
    // Make sure we found a valid visible section
    if (targetIndex >= programContainer.ListOfFormSections.length) {
      console.log(`[advanceAfterCxpAccountCreation] Not advancing - no visible section found after public secure section ${publicSecureIndex}`);
      return false;
    }

    // Advance to the next visible section
    console.log(`[advanceAfterCxpAccountCreation] ADVANCING from section ${publicSecureIndex} to ${targetIndex} after CXP account creation`);
    
    // Consume the flag now that we're using it
    consumeAdvanceFlag();
    
    this.setState({ stepperIndex: targetIndex }, () => {
      console.log('[advanceAfterCxpAccountCreation] State updated, new stepperIndex:', this.state.stepperIndex);
      this.updateUrlForStep(targetIndex);

      // Resume tracking after navigation and trigger exactly one pageview for the final section.
      setTimeout(() => {
        sessionStorage.removeItem("gaUploadPause");
        TrackPageView(programContainer);
      }, 100);
    });

    return true;
  }

  private skipToValidSectionOnInitialLoad(programContainer: ProgramContainer): void {
    console.log('[skipToValidSectionOnInitialLoad] Called');
    
    // Only run this on initial page load, not during navigation
    // Check if user is already signed in from CXP
    const isSignedIn = !!(
      programContainer.Profile?.token || 
      programContainer.Profile?.userId || 
      sessionStorage.getItem("userProfile")
    );
    
    console.log('[skipToValidSectionOnInitialLoad] isSignedIn:', isSignedIn);
    
    if (!isSignedIn) {
      console.log('[skipToValidSectionOnInitialLoad] Not skipping - user not signed in');
      sessionStorage.removeItem("gaUploadPause");
      // Track the current page since we're not skipping
      TrackPageView(programContainer);
      return;
    }

    //only OER program with PS auth should have the case
    //where user is signed in from CXP but has not accepted terms in PS, 
    //and in that case we should not skip sections until they accept terms.
     if(isSignedIn && 
      programContainer.Entity?.accepted !== true && 
      programContainer.AuthRequired === 'ps') {
      console.log('[advanceAfterCxpAccountCreation] Not advancing - user has not accepted terms after returning from CXP');
      return;
    }

    // Get current section index from URL
    const currentPath = window.location.pathname;
    const segments = currentPath.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    const currentSectionIndex = programContainer.ListOfFormSections.findIndex(section => {
      const slugify = (title?: string) => {
        if (!title) return "";
        return title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");
      };
      return slugify(section.ENUTitle) === lastSegment || 
             slugify(section.FRATitle) === lastSegment || 
             slugify(section.pageName) === lastSegment;
    });
    
    console.log('[skipToValidSectionOnInitialLoad] currentSectionIndex:', currentSectionIndex);
    
    if (currentSectionIndex < 0 || currentSectionIndex >= programContainer.ListOfFormSections.length) {
      console.log('[skipToValidSectionOnInitialLoad] Invalid section index');
      sessionStorage.removeItem("gaUploadPause");
      return;
    }
    
    const currentSection = programContainer.ListOfFormSections[currentSectionIndex];
    const publicSecureSection = getPublicSecurePage(programContainer.ListOfFormSections);
    const isCurrentPublicSecureSection = !!publicSecureSection && currentSection === publicSecureSection;
    
    console.log('[skipToValidSectionOnInitialLoad] Current section:', currentSection.ENUTitle, 'SkipPageViewTracking:', currentSection.SkipPageViewTracking, 'Visible:', currentSection.Visible, 'isPublicSecureSection:', isCurrentPublicSecureSection);
    
    // Check if current section should be skipped (for CXP users)
    // Skip if section has SkipPageViewTracking OR is invisible OR is the Public Secure section.
    const shouldSkipCurrentSection =
      currentSection.SkipPageViewTracking ||
      currentSection.Visible === false ||
      isCurrentPublicSecureSection;
    
    if (!shouldSkipCurrentSection) {
      console.log('[skipToValidSectionOnInitialLoad] Current section should not be skipped - tracking current page');
      sessionStorage.removeItem("gaUploadPause");
      TrackPageView(programContainer);
      return;
    }
    
    console.log('[skipToValidSectionOnInitialLoad] Current section should be skipped - finding next valid section');
    
    // Find the next valid section
    let targetIndex = currentSectionIndex;
    
    while (targetIndex < programContainer.ListOfFormSections.length) {
      const section = programContainer.ListOfFormSections[targetIndex];
      
      // Skip if section is hidden
      if (section?.Visible === false) {
        console.log('[skipToValidSectionOnInitialLoad] Section', targetIndex, 'is hidden, skipping');
        targetIndex++;
        continue;
      }
      
      // Skip sections with SkipPageViewTracking flag (for signed-in CXP users)
      if (section?.SkipPageViewTracking) {
        console.log('[skipToValidSectionOnInitialLoad] Section', targetIndex, `("${section.ENUTitle}") has SkipPageViewTracking=true - skipping`);
        targetIndex++;
        continue;
      }

      // Skip the Public Secure section after OAuth return for signed-in users.
      if (publicSecureSection && section === publicSecureSection) {
        console.log('[skipToValidSectionOnInitialLoad] Section', targetIndex, `("${section.ENUTitle}") is Public Secure - skipping`);
        targetIndex++;
        continue;
      }
      
      // Found a valid section to navigate to
      break;
    }
    
    console.log('[skipToValidSectionOnInitialLoad] Final targetIndex:', targetIndex);
    
    // If we found a different section, navigate to it
    if (targetIndex !== currentSectionIndex && targetIndex < programContainer.ListOfFormSections.length) {
      console.log(`[skipToValidSectionOnInitialLoad] SKIPPING from section ${currentSectionIndex} to ${targetIndex}`);
      
      // gaUploadPause is already set, keep it during navigation
      
      this.setState({ stepperIndex: targetIndex }, () => {
        console.log('[skipToValidSectionOnInitialLoad] State updated, new stepperIndex:', this.state.stepperIndex);
        this.updateUrlForStep(targetIndex);
        
        // Resume tracking after navigation completes
        setTimeout(() => {
          console.log('[skipToValidSectionOnInitialLoad] setTimeout callback firing');
          console.log('[skipToValidSectionOnInitialLoad] Current URL:', window.location.href);
          console.log('[skipToValidSectionOnInitialLoad] Current pathname:', window.location.pathname);
          sessionStorage.removeItem("gaUploadPause");
          console.log('[skipToValidSectionOnInitialLoad] Removed gaUploadPause, triggering page view for final section');
          // Manually track the final valid section
          TrackPageView(programContainer);
          console.log('[skipToValidSectionOnInitialLoad] TrackPageView call completed');
        }, 100);
      });
    } else {
      console.log('[skipToValidSectionOnInitialLoad] No need to skip - already on valid section or no valid section found');
      sessionStorage.removeItem("gaUploadPause");
      TrackPageView(programContainer);
    }
  }
}
